import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Center, Spinner, Text, Flex, Image } from '@chakra-ui/react';
import {
  TableOptions,
  DataTable,
  StatusBadge,
  TabMenu,
  type TabItem,
  type ToggleOption,
  type Column,
  type StatusType,
} from '@themes/aegix';
import {
  useAlertType,
  useAlertTypes,
  useDeleteAlertType,
  useRestoreAlertType,
} from '../../hooks/queries';
import { useSites } from '../../hooks/queries';
import type { AlertType } from '../../services';
import { ProtocolDrawer } from './drawers/ProtocolDrawer';
import { DistrictDefaultCellsEditor } from './DistrictDefaultCellsEditor';
import { HiChevronDown, HiChevronRight } from 'react-icons/hi2';
import { MdNotificationsActive, MdDescription } from 'react-icons/md';
import { toaster } from '../ui/toaster';
import { useSearchParams } from 'react-router-dom';
import { useHasPermission } from '@aegix/auth-permissions';
import { useActiveOrganization } from '@aegix/auth-organization';
import type { ActiveOrganization } from '../../lib/sms-auth';

// `follow` is an incident-only urgency (per ADR-068 + the
// backend's `ALERT_URGENCY_TYPES` constant) and isn't an "alert" in the
// customer-facing nomenclature. The Protocols urgency filter should
// only expose alert-eligible urgencies. Alert types created with
// urgency='follow' still appear under "All".

interface ProtocolRow {
  [key: string]: unknown;
  id: string;
  icon: string | null;
  name: string;
  urgency: string;
  activation_mode: string;
  requires_approval: boolean;
  has_groups: boolean;
  show_all: boolean;
  is_archived: boolean;
  status: StatusType;
}

function transformAlertTypes(types: AlertType[]): ProtocolRow[] {
  return types.map((t) => ({
    id: String(t.id),
    icon: t.icon ?? null,
    name: t.name,
    urgency: t.urgency,
    activation_mode: t.activation_mode,
    requires_approval: t.requires_admin_approval ?? false,
    has_groups: (t.alert_to_specific_groups_only?.length ?? 0) > 0,
    show_all:
      !t.alert_to_specific_groups_only?.length ||
      (t.can_start_others_group_alert ?? false),
    is_archived: t.deleted !== null && t.deleted !== undefined,
    status: t.deleted
      ? 'inactive'
      : t.is_active
        ? ('active' as StatusType)
        : ('inactive' as StatusType),
  }));
}

function CheckIndicator({ checked }: { checked: boolean }) {
  return (
    <Box
      w="16px"
      h="16px"
      borderRadius="sm"
      border="2px solid"
      borderColor={checked ? 'brand.500' : 'border.secondary'}
      bg={checked ? 'brand.500' : 'transparent'}
      mx="auto"
    />
  );
}

interface SiteGroup {
  siteId: number;
  siteName: string;
  protocols: ProtocolRow[];
  alertTypes: AlertType[];
}

export function ProtocolsList() {
  const { t } = useTranslation();

  const urgencyToggleOptions: ToggleOption[] = useMemo(() => [
    { value: 'all', label: t('common.all', 'All'), 'data-e2e': 'protocol-filter-all' },
    { value: 'emergency', label: t('protocols.emergency', 'Emergency'), 'data-e2e': 'protocol-filter-emergency' },
    { value: 'attention', label: t('protocols.attention', 'Attention'), 'data-e2e': 'protocol-filter-attention' },
  ], [t]);
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const urlTab = searchParams.get('tab');
  const isProtocolsTab = !urlTab || urlTab === 'protocols';
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [expandedSites, setExpandedSites] = useState<Set<number>>(new Set());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProtocol, setEditingProtocol] = useState<AlertType | null>(
    null,
  );

  // Page-level scope shift. Two scopes:
  //   - 'site' (default) — per-site protocol table
  //   - 'district' — district-level template defaults (the cross-protocol
  //     `(urgency × mode)` cell editor, `DistrictDefaultCellsEditor`)
  //
  // The org is always unambiguous in this app — typical user has one;
  // vendor/internal user picks via the VendorOrgSelector in the header.
  // We pull the active org from `@aegix/auth-organization` and use its id
  // as the district identifier for the rubric API.
  //
  // Persisted in the URL via `?scope=district` so a deep link survives
  // refresh and back/forward. Mirrors the `?tab=` pattern this page
  // already uses for the Sites tab.
  // SMS always thinks of `activeOrganization.id` as a numeric district
  // id (Aegix-internal Postgres FK). The lib's `Organization.id` is
  // widened to `string | number` for future GUID compatibility; we
  // narrow back to the SMS type at the consume site so downstream
  // components (DistrictDefaultCellsEditor, etc.) keep their `number`
  // props.
  const activeOrganization = useActiveOrganization() as ActiveOrganization | null;
  const canEditDistrictRubric = useHasPermission(
    'schools.change_districtalertrubriccell',
  );
  const urlScope = searchParams.get('scope');
  const scope: 'site' | 'district' =
    urlScope === 'district' && canEditDistrictRubric && activeOrganization
      ? 'district'
      : 'site';
  const setScope = (next: 'site' | 'district') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'district') {
      params.set('scope', 'district');
    } else {
      params.delete('scope');
    }
    setSearchParams(params);
  };

  // Match the platform 3rd-tier nav pattern (TabMenu) used by
  // People > Users/Roles/SSO.
  //
  // The District defaults tab only appears for users with
  // `change_districtalertrubriccell` AND an active organization.
  // TabMenu auto-hides when `tabs.length <= 1`, so when neither
  // condition is met the tab row disappears naturally — no explicit
  // `showScopeToggle` gate needed.
  const scopeTabs: TabItem[] = useMemo(
    () => [
      {
        value: 'site',
        label: t('protocols.siteProtocols', 'Site protocols'),
        'data-e2e': 'protocols-scope-site',
      },
      ...(canEditDistrictRubric && activeOrganization
        ? [
            {
              value: 'district',
              label: t('protocols.defaultRecipients', 'Default Recipients'),
              'data-e2e': 'protocols-scope-district',
            } as TabItem,
          ]
        : []),
    ],
    [canEditDistrictRubric, activeOrganization, t],
  );

  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const handleFilterChange = useCallback((column: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [column]: value }));
  }, []);

  const handleFilterClear = useCallback((column: string) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      delete next[column];
      return next;
    });
  }, []);

  const filterProtocols = useCallback(
    (protocols: ProtocolRow[]) => {
      const activeFilters = Object.entries(filterValues).filter(([, v]) => v);
      if (activeFilters.length === 0) return protocols;
      return protocols.filter((row) =>
        activeFilters.every(([col, val]) => {
          const cellValue = String(
            (row as Record<string, unknown>)[col] ?? '',
          ).toLowerCase();
          return cellValue.includes(val.toLowerCase());
        }),
      );
    },
    [filterValues],
  );

  const { data: sitesData, isLoading: sitesLoading } = useSites({
    page_size: 100,
  });

  const { data: alertTypesData, isLoading: alertTypesLoading } = useAlertTypes({
    page_size: 500,
    search: searchValue || undefined,
    deleted: showArchived ? undefined : false,
    ordering: 'urgency_order,sort_order,name',
    include_system: true,
  });
  const { data: alertTypesDataById } = useAlertType(Number(urlId));
  const deleteMutation = useDeleteAlertType();
  const restoreMutation = useRestoreAlertType();

  const isLoading = sitesLoading || alertTypesLoading;

  const columns = useMemo(() => {
    const cols: Column<ProtocolRow>[] = [
      {
        key: 'icon',
        label: t('protocols.icon', 'Icon'),
        sortable: false,
        width: '60px',
        render: (row) =>
          row.icon ? (
            <Image
              src={row.icon as string}
              w="32px"
              h="32px"
              objectFit="contain"
              mx="auto"
            />
          ) : (
            <Center
              w="32px"
              h="32px"
              mx="auto"
              bg="surface.tertiary"
              borderRadius="md"
            >
              <MdNotificationsActive size={16} />
            </Center>
          ),
      },
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: false,
        filterable: true,
        width: '200px',
      },
      {
        key: 'urgency',
        label: t('protocols.urgency', 'Urgency'),
        sortable: false,
        filterable: true,
        filterOptions: [
          { label: t('protocols.emergency', 'Emergency'), value: 'emergency' },
          { label: t('protocols.attention', 'Attention'), value: 'attention' },
        ],
        width: '120px',
        render: (row) => (
          <Text textTransform="capitalize" fontSize="sm">
            {t(`protocols.${row.urgency}`, row.urgency as string)}
          </Text>
        ),
      },
      {
        key: 'activation_mode',
        label: t('protocols.activate', 'Activate'),
        sortable: false,
        filterable: true,
        filterOptions: [
          { label: t('events.toggle.alerts', 'Alert'), value: 'alert' },
          { label: t('protocols.incident', 'Incident'), value: 'incident' },
        ],
        width: '80px',
        render: (row) => (
          <Center>
            {row.activation_mode === 'alert' ? (
              <MdNotificationsActive size={18} title={t('events.toggle.alerts', 'Alert')} />
            ) : (
              <MdDescription size={18} title={t('protocols.incident', 'Incident')} />
            )}
          </Center>
        ),
      },
      {
        key: 'requires_approval',
        label: t('protocols.approve', 'Approve'),
        sortable: false,
        width: '80px',
        render: (row) => (
          <CheckIndicator checked={row.requires_approval as boolean} />
        ),
      },
      {
        key: 'has_groups',
        label: t('protocols.group', 'Group'),
        sortable: false,
        width: '80px',
        render: (row) => <CheckIndicator checked={row.has_groups as boolean} />,
      },
      {
        key: 'show_all',
        label: t('protocols.showAll', 'Show All'),
        sortable: false,
        width: '80px',
        render: (row) => <CheckIndicator checked={row.show_all as boolean} />,
      },
    ];

    if (showArchived) {
      cols.push({
        key: 'status',
        label: t('common.status', 'Status'),
        sortable: false,
        width: '100px',
        render: (row) => (
          <StatusBadge
            status={row.is_archived ? 'inactive' : ('active' as StatusType)}
            label={row.is_archived ? t('protocols.archived', 'Archived') : t('common.active', 'Active')}
          />
        ),
      });
    }

    return cols;
  }, [showArchived, t]);

  const siteGroups = useMemo(() => {
    const isSingleProtocolView = Boolean(
      urlId && isProtocolsTab && alertTypesDataById,
    );
    const alertTypesList = isSingleProtocolView
      ? [alertTypesDataById as AlertType]
      : alertTypesData?.results;

    if (!sitesData?.results || !alertTypesList) return [];

    const groups: SiteGroup[] = [];
    const alertTypesBySchool = new Map<number, AlertType[]>();

    for (const at of alertTypesList) {
      const list = alertTypesBySchool.get(at.school) || [];
      list.push(at);
      alertTypesBySchool.set(at.school, list);
    }

    for (const site of sitesData.results) {
      const siteAlertTypes = alertTypesBySchool.get(site.id) || [];
      let filtered = siteAlertTypes;
      if (urgencyFilter !== 'all') {
        filtered = filtered.filter((at) => at.urgency === urgencyFilter);
      }

      if (filtered.length > 0 || (!searchValue && !isSingleProtocolView)) {
        groups.push({
          siteId: site.id,
          siteName: site.name,
          protocols: transformAlertTypes(filtered),
          alertTypes: filtered,
        });
      }
    }

    return groups;
  }, [
    sitesData,
    alertTypesData,
    alertTypesDataById,
    urgencyFilter,
    searchValue,
    urlId,
    isProtocolsTab,
  ]);

  const toggleSite = (siteId: number) => {
    setExpandedSites((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) {
        next.delete(siteId);
      } else {
        next.add(siteId);
      }
      return next;
    });
  };

  // Row click is the single open affordance — there is no per-row
  // edit-icon column. Archived rows open the drawer too (no
  // early-return) so Restore Protocol stays reachable.
  const handleRowClick = (row: ProtocolRow, siteGroup: SiteGroup) => {
    const alertType = siteGroup.alertTypes.find(
      (at) => String(at.id) === row.id,
    );
    if (alertType) {
      setEditingProtocol(alertType);
      setDrawerOpen(true);
    }
  };

  const handleAddProtocol = () => {
    setEditingProtocol(null);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingProtocol(null);
  };

  const handleArchive = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toaster.create({
          title: 'Protocol archived',
          type: 'success',
          duration: 3000,
        });
      },
      onError: (err) => {
        toaster.create({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to archive',
          type: 'error',
          duration: 4000,
        });
      },
    });
  };

  const handleRestore = (id: number) => {
    restoreMutation.mutate(id, {
      onSuccess: () => {
        toaster.create({
          title: 'Protocol restored',
          type: 'success',
          duration: 3000,
        });
      },
      onError: (err) => {
        toaster.create({
          title: 'Error',
          description: err instanceof Error ? err.message : 'Failed to restore',
          type: 'error',
          duration: 4000,
        });
      },
    });
  };

  return (
    <>
      <TabMenu
        tabs={scopeTabs}
        activeTab={scope}
        onChange={(next) => setScope(next as 'site' | 'district')}
        e2eTitle="protocols-scope"
      />

      {scope === 'district' && activeOrganization && (
        <Box px={5} pb={5}>
          <DistrictDefaultCellsEditor districtId={activeOrganization.id} />
        </Box>
      )}

      {scope === 'site' && (
        <>
          <TableOptions
            toggleOptions={urgencyToggleOptions}
            activeToggle={urgencyFilter}
            onToggleChange={(v: string) => setUrgencyFilter(v)}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            showFilter={false}
            primaryAction={{
              label: t('protocols.addProtocol', 'Add Protocol'),
              onClick: handleAddProtocol,
            }}
          />

          <Box px={5} pb={5}>
        {/* Show Archived toggle */}
        <Flex mb={3} justifyContent="flex-end">
          <Flex
            alignItems="center"
            gap={2}
            cursor="pointer"
            onClick={() => setShowArchived(!showArchived)}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowArchived(!showArchived);
              }
            }}
            data-e2e="show-archived-toggle"
          >
            <Box
              w="16px"
              h="16px"
              borderRadius="sm"
              border="2px solid"
              borderColor={showArchived ? 'brand.500' : 'border.secondary'}
              bg={showArchived ? 'brand.500' : 'transparent'}
            />
            <Text fontSize="sm" color="text.secondary">
              {t('protocols.showArchived', 'Show Archived')}
            </Text>
          </Flex>
        </Flex>

        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : siteGroups.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('protocols.noProtocolsFound', 'No protocols found')}</Text>
          </Center>
        ) : (
          <Box>
            {siteGroups.map((group) => {
              const isExpanded = expandedSites.has(group.siteId);

              return (
                <Box
                  key={group.siteId}
                  mb={3}
                  border="1px solid"
                  borderColor="border.secondary"
                  borderRadius="lg"
                  overflow="hidden"
                >
                  <Flex
                    px={4}
                    py={3}
                    alignItems="center"
                    justifyContent="space-between"
                    cursor="pointer"
                    bg="surface.secondary"
                    _hover={{ bg: 'surface.tertiary' }}
                    onClick={() => toggleSite(group.siteId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleSite(group.siteId);
                      }
                    }}
                    data-e2e={`protocol-site-row-${group.siteId}`}
                  >
                    <Flex alignItems="center" gap={2}>
                      {isExpanded ? (
                        <HiChevronDown size={18} />
                      ) : (
                        <HiChevronRight size={18} />
                      )}
                      <Text fontWeight={600} fontSize="sm" color="text.primary">
                        {group.siteName}
                      </Text>
                    </Flex>
                    <Text fontSize="xs" color="text.secondary">
                      {group.protocols.length} protocol
                      {group.protocols.length !== 1 ? 's' : ''}
                    </Text>
                  </Flex>

                  {isExpanded && (
                    <Box>
                      {group.protocols.length === 0 ? (
                        <Center py={6}>
                          <Text fontSize="sm" color="text.secondary">
                            No protocols for this site
                          </Text>
                        </Center>
                      ) : (
                        <DataTable
                          columns={columns}
                          data={filterProtocols(group.protocols)}
                          keyExtractor={(row) => row.id}
                          filterValues={filterValues}
                          onFilterChange={handleFilterChange}
                          onFilterClear={handleFilterClear}
                          onRowClick={(row) => handleRowClick(row, group)}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
          </Box>
        </>
      )}

      <ProtocolDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        protocol={editingProtocol}
        onArchive={handleArchive}
        onRestore={handleRestore}
      />
    </>
  );
}
